import type { SetStateAction } from 'react';

import { atom, type PrimitiveAtom, type WritableAtom } from 'jotai';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- Jotai RESET is a `unique symbol`; atom generics need `typeof RESET`, which requires a value binding
import { atomWithReset, RESET } from 'jotai/utils';

import type { StartBenchmarkRequest } from '../api/multiRun';
import type {
	BenchmarkArm,
	BenchmarkErrorEventData,
	BenchmarkEvent,
	BenchmarkExcludedMcpServer,
	BenchmarkRequestFailure,
	BenchmarkResultEventData,
	BenchmarkTokenUsage,
} from '../api/types';
import type { BenchmarkErrorReport } from './errorReport';
import { benchmarkTranscriptAtom, createTranscriptState } from './transcriptState';

/**
 * `waiting` means this arm has not emitted its start event yet.
 */
export type BenchmarkArmStatus = 'waiting' | 'running' | 'completed' | 'failed';

export type BenchmarkArmState = {
	status: BenchmarkArmStatus;
	/** From the `arm` event. Opaque: never switch on the value. */
	model: string | null;
	startedAtMs: number | null;
	durationMs: number | null;
	/**
	 * Arrives in one step when the arm finishes. There is no incremental usage
	 * event, so this cannot tick upward while the arm is running.
	 */
	usage: BenchmarkTokenUsage | null;
	/** Calls made, counted from `tool` events while the run streams. */
	startedToolCount: number;
};

/**
 * `provisioning` covers the gap between the start request and the first event.
 * `complete` and `failed` are terminal; there is no lifecycle phase for either,
 * because the `result` and `error` events signal them.
 */
type BenchmarkRunPhase =
	| 'provisioning'
	| 'accepted'
	| 'planning'
	| 'arms_running'
	| 'judging'
	| 'complete'
	| 'failed';

export type BenchmarkRunState = {
	phase: BenchmarkRunPhase;
	arms: Record<BenchmarkArm, BenchmarkArmState>;
	/** Highest sequence applied, which is the stream cursor. */
	lastSequence: number;
};

/** A run's complete identity, as represented in the URL. */
type BenchmarkRunKey = {
	runId: string;
	cloudId: string;
};

/**
 * Everything needed to re-attach to a run, and to render its header and prompt,
 * without asking the service for any of it.
 *
 * `request` is kept because the service never returns the rendered prompt, so
 * the prompt panel is reconstructed from the inputs. `eventsUrl` is kept
 * because the start response's value is authoritative about the diagnostics
 * tier, and rebuilding it by hand risks a tier mismatch.
 *
 * A run adopted from its URL rather than started here knows only what the
 * service will tell it, which is why several fields are nullable: a shared
 * link, a second tab and a reload in a cleared tab all arrive with nothing but
 * the tenant and the run id.
 */
export type BenchmarkRunContext = BenchmarkRunKey & {
	/**
	 * The tenant's hostname as it was at submit time, for example
	 * `hello.atlassian.net`. `null` for a run adopted from its URL and for a run
	 * started before any site resolved, so every reader has to tolerate its
	 * absence.
	 */
	siteHostname: string | null;
	/**
	 * What was asked for. `null` for a run adopted from its URL: the service
	 * does not return a run's inputs, so the prompt cannot be rebuilt and is
	 * taken from the transcript stream instead, which carries it verbatim.
	 */
	request: StartBenchmarkRequest | null;
	eventsUrl: string;
	excludedMcpServers?: readonly BenchmarkExcludedMcpServer[];
	mcpServerNamesByAri?: Readonly<Record<string, string>>;
	/**
	 * Client clock at submit, before the service has accepted anything. `null`
	 * for a run adopted from its URL, which this tab never submitted and whose
	 * start time the service does not report.
	 */
	startedAtIso: string | null;
	/**
	 * Client clock at the first response that updated an existing session (this
	 * tab's start response, or a later status or cancel response) and reported
	 * this prompt past provisioning and not yet finished. `null` until then,
	 * which includes the first load of a run adopted from its URL.
	 *
	 * Nothing times the run from it: the run progress clock counts from
	 * `MultiBenchmarkSession.startedAtIso`, which is when this tab's start
	 * call returned successfully, or the service's `createdAt` for an adopted
	 * run.
	 */
	confirmedAtIso?: string | null;
};

/** How a run ended, tagged with the run it belongs to. */
type BenchmarkRunOutcome =
	| (BenchmarkRunKey & { kind: 'result'; result: BenchmarkResultEventData })
	| (BenchmarkRunKey & { kind: 'error'; error: BenchmarkErrorEventData });

/**
 * How the browser's link to the run is faring, which is separate from how the
 * run itself is faring. `lost` and `unavailable` mean the UI stopped receiving
 * events; neither implies the run stopped. `paused` means this browser chose not
 * to stream the prompt for now, to stay within its connection budget.
 */
export type BenchmarkConnectionStatus =
	| 'idle'
	| 'paused'
	| 'connecting'
	| 'streaming'
	| 'reconnecting'
	| 'lost'
	| 'unavailable'
	| 'access-denied';

const createInitialArmState = (): BenchmarkArmState => ({
	status: 'waiting',
	model: null,
	startedAtMs: null,
	durationMs: null,
	usage: null,
	startedToolCount: 0,
});

export const createInitialBenchmarkRunState = (): BenchmarkRunState => ({
	phase: 'provisioning',
	arms: { control: createInitialArmState(), twg: createInitialArmState() },
	lastSequence: 0,
});

const parseEventTime = (timestamp: string): number | null => {
	const parsed = Date.parse(timestamp);

	return Number.isNaN(parsed) ? null : parsed;
};

const withArm = (
	state: BenchmarkRunState,
	arm: BenchmarkArm,
	update: (armState: BenchmarkArmState) => BenchmarkArmState,
): BenchmarkRunState => ({
	...state,
	arms: { ...state.arms, [arm]: update(state.arms[arm]) },
});

/**
 * Folds one event into the run's view model.
 *
 * Pure, and deliberately so: replaying the same events in the same order
 * always produces the same state, which is what lets the stream layer replay a
 * journal from the start after a reload, and lets tests and stories build any
 * state by feeding it a captured run.
 *
 * `tool` supplies the call count: it has no invocation id, so its events
 * cannot be paired, but it is emitted for every invocation, which makes it the
 * honest source for "how many calls so far".
 */
export const applyBenchmarkEvent = (
	state: BenchmarkRunState,
	event: BenchmarkEvent,
): BenchmarkRunState => {
	// A rollover marker reuses the current sequence as its id and carries
	// nothing to render, so it must not touch the cursor.
	if (event.name === 'reconnect') {
		return state;
	}

	const eventAtMs = parseEventTime(event.data.timestamp);
	const base: BenchmarkRunState = {
		...state,
		lastSequence: Math.max(state.lastSequence, event.data.sequence),
	};

	switch (event.name) {
		case 'transcript':
			return base;

		case 'heartbeat':
			// Liveness only. The watchdog reads it; nothing renders it.
			return base;

		case 'lifecycle':
			return base.phase === 'complete' || base.phase === 'failed'
				? base
				: { ...base, phase: event.data.phase };

		case 'arm': {
			const { arm, status, model, durationMs, usage } = event.data;

			return withArm(base, arm, (armState) =>
				status === 'started'
					? {
							...armState,
							status: 'running',
							model,
							startedAtMs: eventAtMs ?? armState.startedAtMs,
						}
					: {
							...armState,
							status: status === 'completed' ? 'completed' : 'failed',
							model,
							durationMs: durationMs ?? armState.durationMs,
							usage: usage ?? armState.usage,
						},
			);
		}

		case 'tool': {
			const { arm, status } = event.data;

			return withArm(base, arm, (armState) => ({
				...armState,
				startedToolCount: armState.startedToolCount + (status === 'started' ? 1 : 0),
			}));
		}

		case 'trace':
			// Per-call trace records. This screen counts calls from `tool` events instead.
			return base;

		case 'diagnostic':
			// Structured per-arm telemetry for an expert panel. This screen does
			// not render it, and the arm summary it repeats is already covered by
			// the `arm` event.
			return base;

		case 'result':
			return { ...base, phase: 'complete' };

		case 'error':
			return { ...base, phase: 'failed' };
	}
};

export const benchmarkRunStateAtom: PrimitiveAtom<BenchmarkRunState> & {
	init: BenchmarkRunState;
} = atom<BenchmarkRunState>(createInitialBenchmarkRunState());

/**
 * How the run in hand ended, for as long as this page is mounted.
 *
 * No longer the retention story, which is what it used to be: benchmark output
 * is not stored client-side any more, and
 * `GET /gateway/api/twg-api-v2/{cloudId}/v1/microsite/benchmark/runs/{runId}/report`
 * outlives both the ten-minute event journal and the tab. This is now only a
 * within-session record of a terminal run, so the comparison and the
 * transcript can be drawn without asking for the report a second time. A
 * reload refills it from the report rather than from storage.
 */
export const benchmarkRunOutcomeAtom: WritableAtom<
	BenchmarkRunOutcome | null,
	[SetStateAction<BenchmarkRunOutcome | null> | typeof RESET],
	void
> = atomWithReset<BenchmarkRunOutcome | null>(null);

export const benchmarkConnectionStatusAtom: PrimitiveAtom<BenchmarkConnectionStatus> & {
	init: BenchmarkConnectionStatus;
} = atom<BenchmarkConnectionStatus>('idle');

/** A failed start or cancel, held so the view can explain itself. */
export const benchmarkRequestFailureAtom: PrimitiveAtom<BenchmarkRequestFailure | null> & {
	init: BenchmarkRequestFailure | null;
} = atom<BenchmarkRequestFailure | null>(null);

/**
 * A reconnect that keeps failing raises a report each time it gives up, so the
 * list is capped: a stack of flags tall enough to cover the run is worse than
 * no flag at all, and the oldest report is the least useful one to escalate.
 */
const MAX_BENCHMARK_ERROR_REPORTS = 3;

/**
 * Failures worth escalating, newest first.
 *
 * Newest first because the flag group treats the first entry as the topmost
 * one and the only dismissable one, and the failure someone is reacting to is
 * the one that just happened.
 *
 * Deliberately not persisted. A trace id is only worth anything while the
 * person still has the failure in front of them, so rehydrating one after a
 * reload would put a stale flag over a working page.
 */
export const benchmarkErrorReportsAtom: PrimitiveAtom<readonly BenchmarkErrorReport[]> & {
	init: readonly BenchmarkErrorReport[];
} = atom<readonly BenchmarkErrorReport[]>([]);

export const pushBenchmarkErrorReportAtom: WritableAtom<null, [BenchmarkErrorReport], void> = atom(
	null,
	(get, set, report: BenchmarkErrorReport) => {
		set(
			benchmarkErrorReportsAtom,
			[report, ...get(benchmarkErrorReportsAtom)].slice(0, MAX_BENCHMARK_ERROR_REPORTS),
		);
	},
);

export const dismissBenchmarkErrorReportAtom: WritableAtom<null, [string | number], void> = atom(
	null,
	(get, set, id: string | number) => {
		set(
			benchmarkErrorReportsAtom,
			get(benchmarkErrorReportsAtom).filter((report) => report.id !== id),
		);
	},
);

/**
 * Clears the state associated with the previously addressed run.
 *
 * Local only: a run that has not finished carries on executing in its sandbox,
 * so a caller releasing a live run rather than cancelling it is choosing to
 * leave it unwatched. Reviews are stored separately by run ID and survive.
 *
 * Shared rather than written out at each call site so route changes cannot
 * leave one run's timeline, failures or transcript under another run's URL.
 */
export const releaseBenchmarkRunAtom: WritableAtom<null, [], void> = atom(null, (_get, set) => {
	set(benchmarkRunOutcomeAtom, RESET);
	set(benchmarkRunStateAtom, createInitialBenchmarkRunState());
	set(benchmarkTranscriptAtom, createTranscriptState());
	set(benchmarkConnectionStatusAtom, 'idle');
	set(benchmarkRequestFailureAtom, null);
	set(benchmarkErrorReportsAtom, []);
});
